import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

export type CableFeature = {
  id: string;
  color: string;
  type: string;
  points: [number, number][];
};

type Props = {
  cables: CableFeature[];
  editMode?: boolean;
  onEdited: (id: string, points: [number, number][]) => void;
  onRemoved: (id: string) => void;
};

const toCssColor = (c: string): string => {
  const dict: Record<string, string> = {
    црвена: "#ff0000",
    плава: "#0057ff",
    зелена: "#00a652",
    жута: "#ffcc00",
    црна: "#000000",
    бела: "#ffffff",
    помаранџаста: "#ff8c00",
    сива: "#888888",
    кафена: "#8b4513",
    розева: "#ff69b4",
    лилакова: "#8a2be2",
    цијан: "#00ffff",
    магента: "#ff00ff",
    индиго: "#4b0082",
    амбер: "#ffbf00",
    "светло плава": "#7db7ff",
    "светло зелена": "#7ed957",
    "светло жута": "#fff176",
    "светло помаранџаста": "#ffb74d",
    "светло лилакова": "#b388ff",
    "светло сива": "#bdbdbd",
    "светло розева": "#ff9ecb",
    "светло цијан": "#80ffff",
    "светло магента": "#ff80ff",
    "светло индиго": "#8f6fff",
    "светло амбер": "#ffd54f",
  };

  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c)) return c;

  return dict[c] ?? "#ff0000";
};

const weightFromType = (type: string): number => {
  const matches = type.match(/\d+/g);
  if (!matches) return 3;
  const n = Math.max(...matches.map(Number));

  return Math.min(10, Math.max(2, Math.round(n / 4)));
};

export default function CableLayer({
  cables,
  editMode = false,
  onEdited,
  onRemoved,
}: Props) {
  const map = useMap() as L.Map;

  useEffect(() => {
    const layers: L.Polyline[] = [];

    for (const c of cables) {
      const layer = L.polyline(c.points, {
        weight: weightFromType(c.type),
        color: toCssColor(c.color),
      }).addTo(map);

      (layer as any).feature = {
        type: "Feature",
        properties: { id: c.id },
      };

      // Tooltip (hover)
      layer.bindTooltip(`${c.type} • ${c.color}`, {
        direction: "top",
        sticky: true,
        opacity: 0.9,
      });

      const popupHtml = `
              <div style="min-width:180px">
                <div style="font-weight:600;margin-bottom:6px">Cable</div>
                <div style="font-size:12px;opacity:.8;margin-bottom:8px">
                  Type: ${c.type}<br/>
                  Color: ${c.color}
                </div>
                <button id="del-${c.id}" style="
                  background:#c62828;color:#fff;border:none;border-radius:6px;
                  padding:6px 10px;cursor:pointer
                ">
                  Delete cable
                </button>
              </div>
            `;
      layer.bindPopup(popupHtml);

      // Wire click of the button once popup opens
      const popupOpen = () => {
        const btn = document.getElementById(`del-${c.id}`);
        if (btn) {
          btn.onclick = (e) => {
            e.preventDefault();
            onRemoved(c.id);
            // close popup so UI doesn't linger
            layer.closePopup();
          };
        }
      };
      layer.on("popupopen", popupOpen);

      // Right-click → delete dialog
      const onCtx = () => onRemoved(c.id);
      layer.on("contextmenu", onCtx);

      if ((layer as any).pm) {
        if (editMode) (layer as any).pm.enable();
        else (layer as any).pm.disable();
      }

      const handleEdit = (e: any) => {
        const latlngs = (e.layer as L.Polyline).getLatLngs() as L.LatLng[];
        onEdited(
          c.id,
          latlngs.map((ll) => [ll.lat, ll.lng] as [number, number])
        );
      };

      layer.on("pm:edit", handleEdit);

      const handleRemove = () => {
        const id = (layer as any).feature?.properties?.id;
        if (id && onRemoved) onRemoved(id);
      };
      layer.on("pm:remove", handleRemove);

      layers.push(layer);
      layer.once("remove", () => {
        layer.off("popupopen", popupOpen);
        layer.off("contextmenu", onCtx);
        layer.off("pm:edit", handleEdit);
      });
    }

    // Cleanup: remove listeners & layers created in this render
    return () => {
      layers.forEach((l) => {
        l.remove();
        // l.off("pm:edit");
        // l.off("pm:remove");
        // map.removeLayer(l);
      });
    };
  }, [map, cables, editMode, onEdited, onRemoved]);

  return null;
}
